// services/moleMappingApi.ts

import axios from 'axios';
import AuthService from './AuthService';

const API_BASE = process.env.REACT_APP_API_URL;

// Helper function to get auth headers
async function getAuthHeaders() {
  const authService = new AuthService();
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error("No access token available");
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// Helper function for multipart form data headers
async function getMultipartAuthHeaders() {
  const authService = new AuthService();
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error("No access token available");
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'multipart/form-data'
  };
}

export interface AnnotationPayload {
  annotationId?: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence?: number;
  orderIndex?: number;
  macroImageId: string;
  linkedMicroId?: string;
}

export interface FollowupSessionPayload {
  patientId: string;
  userId: string;
  sessionDate: string;
  baselineSessionId: string;
  notes: string;
  intervalMonths?: number;
}

export interface SessionComparison {
  comparisonId: string;
  totalMoles: number;
  newMoles: number;
  disappearedMoles: number;
  changedMoles: number;
  stableMoles: number;
  highRiskChanges: number;
  overallRiskAssessment: string;
  recommendedAction: string;
  comparisonNotes?: string;
}

export interface AnnotationComparison {
  baselineAnnotationId?: string;
  changeStatus?: string;
  riskLevel?: string;
  sizeChangeMm?: number;
  colorChange?: string;
  shapeChange?: string;
  requiresFollowup?: boolean;
}

export interface SessionWithComparison {
  session: any;
  comparison?: SessionComparison;
  baselineSession?: any;
}

// Create a new mole mapping session
export async function createMoleMapping(
  patientId: string,
  userId: string,
  sessionDate: string,
  notes: string,
  macroImages: File[]
) {
  const formData = new FormData();
  formData.append('patient_id', patientId);
  formData.append('user_id', userId);
  formData.append('session_date', sessionDate);
  formData.append('notes', notes);

  macroImages.forEach((file) => {
    formData.append('macro_images', file);
  });

  const headers = await getMultipartAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/create/`, formData, { headers });

  return response.data; // returns { mappingId }
}

// Add an annotation to a macro image
export async function addAnnotation(annotation: AnnotationPayload) {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/annotate/`, annotation, { headers });
  return response.data; // returns { annotationId }
}

// Update a mole mapping session with new notes and/or images
export async function updateMoleMapping(mappingId: string, notes: string, additionalImages?: File[]) {
  const formData = new FormData();
  formData.append('notes', notes);

  if (additionalImages && additionalImages.length > 0) {
    additionalImages.forEach((file) => {
      formData.append('additional_images', file);
    });
  }

  const headers = await getMultipartAuthHeaders();
  const response = await axios.patch(`${API_BASE}/molemapping/update/${mappingId}`, formData, { headers });
  return response.data;
}

// Delete a mole mapping session
export async function deleteMoleMapping(mappingId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.delete(`${API_BASE}/molemapping/delete/${mappingId}`, { headers });
  return response.data;
}

// Get all mole mappings for a patient
export async function getPatientMoleMappings(patientId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/patient/${patientId}`, { headers });
  return response.data;
}

// Get a specific mole mapping by ID
export async function getMoleMapping(mappingId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/${mappingId}`, { headers });
  return response.data;
}

// Get all macro images for a mole mapping
export async function getMacroImages(mappingId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/${mappingId}/images`, { headers });
  return response.data;
}

// Get all annotations for a macro image
export async function getMappingAnnotations(macroImageId: string) {
  try {
    console.log(`API: Fetching annotations for macro image: ${macroImageId}`);
    console.log(`API URL: ${API_BASE}/molemapping/annotations/${macroImageId}`);
    
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/molemapping/annotations/${macroImageId}`, { headers });
    
    console.log('API Response:', response.data);
    console.log('Response status:', response.status);
    
    return response.data;
  } catch (error: any) {
    console.error('Get annotations error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
}

// Update an annotation
export async function updateAnnotation(annotationId: string, updates: Partial<AnnotationPayload>) {
  const headers = await getAuthHeaders();
  const response = await axios.patch(`${API_BASE}/molemapping/annotate/${annotationId}`, updates, { headers });
  return response.data;
}

// Delete an annotation
export async function deleteAnnotation(annotationId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.delete(`${API_BASE}/molemapping/annotate/${annotationId}`, { headers });
  return response.data;
}

// Upload a micro (dermatoscopic) image
export async function uploadMicroImage(microImage: File) {
  const formData = new FormData();
  formData.append('micro_image', microImage);

  const headers = await getMultipartAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/micro/upload`, formData, { headers });
  return response.data; // returns { microImageId, imageUrl }
}

// Link a micro image to an annotation
export async function linkMicroToAnnotation(annotationId: string, microImageId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/micro/link`, {
    annotationId,
    microImageId
  }, { headers });
  return response.data;
}

// Delete a micro image
export async function deleteMicroImage(microImageId: string) {
  try {
    console.log(`Attempting to delete micro image with ID: ${microImageId}`);
    console.log(`Delete URL: ${API_BASE}/molemapping/micro/${microImageId}`);
    
    const headers = await getAuthHeaders();
    console.log('Using headers:', headers);
    
    const response = await axios.delete(`${API_BASE}/molemapping/micro/${microImageId}`, { headers });
    console.log('Delete response:', response.data);
    
    return response.data;
  } catch (error: any) {
    console.error('Delete micro image error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
}

// Delete a macro image (with cascading annotation deletion)
export async function deleteMacroImage(macroImageId: string) {
  try {
    console.log(`Attempting to delete macro image with ID: ${macroImageId}`);
    console.log(`Delete URL: ${API_BASE}/molemapping/macro/${macroImageId}`);
    
    const headers = await getAuthHeaders();
    console.log('Using headers:', headers);
    
    const response = await axios.delete(`${API_BASE}/molemapping/macro/${macroImageId}`, { headers });
    console.log('Delete macro image response:', response.data);
    
    return response.data;
  } catch (error: any) {
    console.error('Delete macro image error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
}

// Compare annotations across sessions (for tracking changes)
export async function compareAnnotations(previousMappingId: string, currentMappingId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/compare/${previousMappingId}/${currentMappingId}`, { headers });
  return response.data;
}

// Get statistics for a patient's mole mappings over time
export async function getPatientMoleStats(patientId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/stats/${patientId}`, { headers });
  return response.data;
}

// Create a follow-up mole mapping session
export async function createFollowupMoleMapping(
  patientId: string,
  userId: string,
  sessionDate: string,
  baselineSessionId: string,
  notes: string,
  macroImages: File[],
  intervalMonths?: number
) {
  const formData = new FormData();
  formData.append('patient_id', patientId);
  formData.append('user_id', userId);
  formData.append('session_date', sessionDate);
  formData.append('baseline_session_id', baselineSessionId);
  formData.append('notes', notes);
  
  if (intervalMonths !== undefined) {
    formData.append('interval_months', intervalMonths.toString());
  }

  macroImages.forEach((file) => {
    formData.append('macro_images', file);
  });

  const headers = await getMultipartAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/create-followup/`, formData, { headers });

  return response.data;
}

// Add annotation with baseline comparison
export async function addAnnotationWithComparison(
  annotation: AnnotationPayload, 
  comparison?: AnnotationComparison
) {
  const headers = await getAuthHeaders();
  const payload = {
    ...annotation,
    comparison
  };
  const response = await axios.post(`${API_BASE}/molemapping/annotate-with-comparison/`, payload, { headers });
  return response.data;
}

// Compare baseline and follow-up sessions
export async function compareSession(baselineSessionId: string, followupSessionId: string, comparisonNotes?: string) {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API_BASE}/molemapping/compare-sessions/`, {
    baselineSessionId,
    followupSessionId,
    comparisonNotes: comparisonNotes || ""
  }, { headers });
  return response.data;
}

// Get session with comparison data
export async function getSessionWithComparison(sessionId: string): Promise<SessionWithComparison> {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/session-with-comparison/${sessionId}`, { headers });
  return response.data;
}

// Get patient's baseline sessions
export async function getPatientBaselineSessions(patientId: string) {
  try {
    console.log(`Fetching baseline sessions for patient: ${patientId}`);
    console.log(`API URL: ${API_BASE}/molemapping/patient/${patientId}/baseline-sessions`);
    
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_BASE}/molemapping/patient/${patientId}/baseline-sessions`, { headers });
    
    console.log('Baseline sessions response:', response.data);
    console.log('Response status:', response.status);
    
    return response.data;
  } catch (error: any) {
    console.error('Get baseline sessions error:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    
    // Return empty array instead of throwing error
    return [];
  }
}

// Get comparison history for a patient
export async function getPatientComparisonHistory(patientId: string) {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API_BASE}/molemapping/patient/${patientId}/comparisons`, { headers });
  return response.data;
}
